/**
 * Order hooks.
 *
 * Every mutation returns the server's view rather than patching a local copy.
 * Totals, sequence numbers, and the business date are decided server-side and
 * a second implementation here would eventually disagree with the first in
 * front of a guest.
 *
 * The one exception is adding a line, which is optimistic: a waiter tapping
 * through a table should see the item appear immediately, and the server
 * confirms a moment later.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useAttemptKeys } from './attempt';
import type { ApiError } from './client';
import { formatMoney } from './menu';
import { useApi } from './provider';

export type OrderState = 'open' | 'locked_for_payment' | 'paid' | 'closed' | 'cancelled';
export type LineState = 'ordered' | 'fired' | 'voided';
/**
 * How an order is served, which decides what paying for it means.
 *
 * At a table the bill comes last and settling it ends the order. At a counter
 * the bill comes first and the guest then stands and waits, so settling it
 * cannot end anything — `paid` means "waiting at the hatch", and the order is
 * only finished when the bag goes across.
 */
export type ServiceMode = 'table' | 'counter';

export interface OrderLineModifier {
  id: string;
  modifier_option_id: string;
  name: string;
  price: string;
  qty: number;
}

export interface OrderLine {
  id: string;
  item_id: string;
  variant_id: string | null;
  name: string;
  variant_name: string | null;
  seat_no: number | null;
  course_no: number;
  qty: string;
  unit_price: string;
  line_total: string;
  state: LineState;
  note: string | null;
  prep_station: string | null;
  void_reason: string | null;
  /** Taken off this line by hand, and by any meal deal it is part of. */
  line_discount: string;
  combo_discount: string;
  /** The rate band this line falls in on a VAT invoice. */
  tax_class: string;
  modifiers: OrderLineModifier[];
  /** What promotions took off this line, in all. */
  promotion_discount: string;
  /** Which promotions, each with what it took, named as the guest reads it. */
  promotions: OrderPromotion[];
}

/** What one promotion, or one coupon, took off a line or the bill. */
export interface OrderPromotion {
  name: string;
  amount: string;
  promotion_id: string | null;
  /** The code, when a coupon is why it is here. */
  code: string | null;
  /** Null: off the whole bill, or a total for the bill. */
  line_id: string | null;
}

/** A coupon code on a tab, and what it is doing. */
export interface OrderCoupon {
  /** Its use on this tab: what taking it off names. */
  id: string;
  code: string;
  /** What the guest reads: "Welcome back". */
  name: string;
  kind: 'promotion' | 'amount';
  /** What it takes off the bill as it stands. */
  amount: string;
  /** Why it takes nothing off, when it does not. */
  note: string | null;
}

export interface Order {
  id: string;
  location_id: string;
  number: number;
  business_date: string;
  table_id: string | null;
  state: OrderState;
  guest_count: number;
  note: string | null;
  subtotal: string;
  discount_total: string;
  /** What a service charge rule added, after discounts. Inside `total`. */
  service_charge_total: string;
  tax_total: string;
  total: string;
  version: number;
  /** Who started the tab. Never moves: the seller report groups on it. */
  opened_by: string;
  /** Who has it now. Moves when a section is handed over. */
  served_by: string;
  opened_at: string;
  closed_at: string | null;
  /** `table` or `counter`. A counter order is paid for first and waited for. */
  service_mode: ServiceMode;
  /** When it went across the counter. Null on everything but a counter order. */
  collected_at: string | null;
  /**
   * Taken away rather than eaten here. Each line sells under its dish's
   * takeaway tax class while it is, where the dish has one. See `useSetTakeaway`.
   */
  takeaway: boolean;
  lines: OrderLine[];
  /**
   * Money taken off by hand, still live. `discount_total` also counts what
   * meal deals, promotions and a coupon took, which are listed apart.
   */
  discounts: OrderDiscount[];
  /** The guest this tab is for, when the till named one. See `useGuest`. */
  customer_id: string | null;
  /** Null when no service charge rule applies to this tab. */
  service_charge: OrderServiceCharge | null;
  /** What meal deals took. Inside `discount_total`. */
  meal_deal_total: string;
  /**
   * Each promotion and coupon on the bill with what it took in all. Inside
   * `discount_total`; applied by the server, chosen by nobody at the till.
   */
  promotions: OrderPromotion[];
  /** The coupon code on the tab (one at most), even while it takes nothing. */
  coupons: OrderCoupon[];
}

/**
 * The service charge a rule puts on a tab. Still here while a manager has it
 * taken off, so the till can say what came off and offer it back.
 */
export interface OrderServiceCharge {
  /** What the guest reads on the bill: "Service". */
  name: string;
  percent: string;
  /** What it adds. "0.00" while it is taken off. */
  amount: string;
  /** The class it is taxed in. Null: not taxed. */
  tax_class: string | null;
  removed: boolean;
  removed_reason: string | null;
  removed_by: string | null;
}

export interface OrderDiscount {
  id: string;
  /** Null: off the whole bill rather than one dish. */
  line_id: string | null;
  kind: 'percent' | 'amount';
  percent: string | null;
  amount: string;
  reason_code: string;
  reason_label: string;
  note: string | null;
  applied_by: string;
}

export interface LedgerEntry {
  seq: number;
  type: string;
  payload: Record<string, unknown>;
  actor_id: string | null;
  occurred_at: string;
}

export function useOpenOrders(locationId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: ['orders', 'open', locationId ?? null],
    queryFn: ({ signal }) => api.get<Order[]>(`/v1/orders?location_id=${locationId}`, signal),
    enabled: Boolean(locationId),
    // Open tabs change constantly during service, and a terminal that shows a
    // stale table is worse than one that shows a spinner.
    staleTime: 5_000,
    refetchInterval: 15_000,
  });
}

/**
 * Orders at a site in one state, on one business day.
 *
 * The tills ask for open tabs and nothing else; this is for the back office,
 * which needs the tabs that have finished — to reprint a receipt, or to answer
 * "what did table 4 have". Any state but `open` needs a business date, because
 * a restaurant's whole history in one response is not a list anybody reads.
 * A settled tab ends in `closed`.
 */
export function useOrders(
  locationId: string | undefined,
  options: { state: OrderState; businessDate: string | undefined },
) {
  const api = useApi();
  const search = new URLSearchParams({ state: options.state });
  if (locationId) search.set('location_id', locationId);
  if (options.businessDate) search.set('business_date', options.businessDate);
  return useQuery({
    queryKey: ['orders', options.state, locationId ?? null, options.businessDate ?? null],
    queryFn: ({ signal }) => api.get<Order[]>(`/v1/orders?${search.toString()}`, signal),
    enabled: Boolean(locationId) && (options.state === 'open' || Boolean(options.businessDate)),
    staleTime: 15_000,
  });
}

export function useOrder(orderId: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: ['orders', 'detail', orderId],
    queryFn: ({ signal }) => api.get<Order>(`/v1/orders/${orderId}`, signal),
    enabled: Boolean(orderId),
    staleTime: 2_000,
  });
}

export function useOrderLedger(orderId: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: ['orders', 'ledger', orderId],
    queryFn: ({ signal }) => api.get<LedgerEntry[]>(`/v1/orders/${orderId}/ledger`, signal),
    enabled: Boolean(orderId),
  });
}

export interface OpenOrderInput {
  location_id: string;
  guest_count?: number;
  table_id?: string | null;
  note?: string | null;
  /** `counter` needs `orders.qsr_mode`, and refuses a table. */
  service_mode?: ServiceMode;
}

export function useOpenOrder() {
  const api = useApi();
  const queryClient = useQueryClient();
  // One key per attempt: tapping "Open" twice, or again after a timeout that
  // had gone through, gets the first tab back rather than a second one.
  const attempts = useAttemptKeys();
  return useMutation<Order, ApiError, OpenOrderInput>({
    mutationFn: (input) =>
      api.post<Order>('/v1/orders', input, { idempotencyKey: attempts.keyFor(input) }),
    onSuccess: (_order, input) => {
      attempts.succeeded(input);
      invalidateOrders(queryClient);
    },
  });
}

export interface AddLineInput {
  orderId: string;
  item_id: string;
  variant_id?: string | null;
  modifier_option_ids?: string[];
  qty?: string;
  seat_no?: number | null;
  course_no?: number;
  note?: string | null;
}

export function useAddLine() {
  const api = useApi();
  const queryClient = useQueryClient();

  // One key per attempt, not per call. A key minted inside `mutationFn` was
  // new on every tap, so a double tap — or a tap again after a spinner that
  // timed out on a request which had gone through — was a second portion the
  // kitchen cooked and the guest paid for. The same dish asked for again
  // before the first has landed is the same attempt; once it has landed, the
  // next one is another dish. Say "two" with the quantity.
  const attempts = useAttemptKeys();

  return useMutation<OrderLine, ApiError, AddLineInput>({
    mutationFn: (input) => {
      const { orderId, ...body } = input;
      return api.post<OrderLine>(`/v1/orders/${orderId}/lines`, body, {
        idempotencyKey: attempts.keyFor(input),
      });
    },
    onSuccess: (_line, input) => attempts.succeeded(input),
    onSettled: (_data, _error, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ['orders', 'detail', variables.orderId],
      });
      void queryClient.invalidateQueries({ queryKey: ['orders', 'open'] });
    },
  });
}

export function useVoidLine() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, { orderId: string; lineId: string; reason: string }>({
    mutationFn: ({ orderId, lineId, reason }) =>
      api.post<void>(`/v1/orders/${orderId}/lines/${lineId}/void`, { reason }),
    onSettled: (_d, _e, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ['orders', 'detail', variables.orderId],
      });
      void queryClient.invalidateQueries({ queryKey: ['orders', 'ledger', variables.orderId] });
      void queryClient.invalidateQueries({ queryKey: ['orders', 'open'] });
    },
  });
}

export function useChangeLineQuantity() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, { orderId: string; lineId: string; qty: string }>({
    mutationFn: ({ orderId, lineId, qty }) =>
      api.request<void>(`/v1/orders/${orderId}/lines/${lineId}`, {
        method: 'PATCH',
        body: { qty },
      }),
    onSettled: (_d, _e, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ['orders', 'detail', variables.orderId],
      });
      void queryClient.invalidateQueries({ queryKey: ['orders', 'open'] });
    },
  });
}

export interface FiredCourse {
  course_no: number;
  lines_fired: number;
}

/**
 * Send a held course to the kitchen.
 *
 * On a plan with course firing every table line waits on the kitchen screen,
 * marked held and with no clock, until this is called for its course — so a
 * table's mains can be taken with the starters and cooked when the starters
 * are cleared. Answers how many lines went; nought means the course had
 * nothing waiting, which is not an error.
 *
 * 402 below Professional, where there is nothing to fire: lines go to the
 * kitchen as they are taken.
 */
export function useFireCourse() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<FiredCourse, ApiError, { orderId: string; courseNo: number }>({
    mutationFn: ({ orderId, courseNo }) =>
      api.post<FiredCourse>(`/v1/orders/${orderId}/fire`, { course_no: courseNo }),
    onSettled: (_d, _e, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ['orders', 'detail', variables.orderId],
      });
      void queryClient.invalidateQueries({ queryKey: ['orders', 'ledger', variables.orderId] });
      // The "food up" chips read the kitchen, and a fired course is now cooking.
      void queryClient.invalidateQueries({ queryKey: ['kds', 'readiness'] });
    },
  });
}

/**
 * The note on a whole tab — a birthday, an allergy at the table, a guest who
 * wants the bill brought without asking. Null or blank clears it.
 *
 * It stays on the floor: the kitchen reads the notes on lines, which travel
 * with each dish to whichever screen cooks it.
 */
export function useSetOrderNote() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, { orderId: string; note: string | null }>({
    mutationFn: ({ orderId, note }) =>
      api.request<void>(`/v1/orders/${orderId}/note`, {
        method: 'PATCH',
        body: { note: note && note.trim() !== '' ? note.trim() : null },
      }),
    onSettled: (_d, _e, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ['orders', 'detail', variables.orderId],
      });
      void queryClient.invalidateQueries({ queryKey: ['orders', 'open'] });
    },
  });
}

/**
 * Eaten here or taken away, until the bill is paid.
 *
 * "Eat in or take away?" is as often the last question at a counter as the
 * first. It changes tax and nothing else: each line moves to its dish's
 * takeaway class, or back, and the price stays the price. Taking an order away
 * is Professional (402 below it); putting one back is allowed on every plan.
 * A tab at a table is eaten there (409).
 */
export function useSetTakeaway() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<Order, ApiError, { orderId: string; takeaway: boolean }>({
    mutationFn: ({ orderId, takeaway }) =>
      api.request<Order>(`/v1/orders/${orderId}/takeaway`, {
        method: 'PATCH',
        body: { takeaway },
      }),
    onSettled: (_d, _e, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ['orders', 'detail', variables.orderId],
      });
      void queryClient.invalidateQueries({ queryKey: ['orders', 'open'] });
      void queryClient.invalidateQueries({ queryKey: ['orders', 'ledger', variables.orderId] });
    },
  });
}

/** How many are at the table. Zero is allowed: a tab for the bar, say. */
export function useSetGuestCount() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, { orderId: string; guestCount: number }>({
    mutationFn: ({ orderId, guestCount }) =>
      api.request<void>(`/v1/orders/${orderId}/guests`, {
        method: 'PATCH',
        body: { guest_count: guestCount },
      }),
    onSettled: (_d, _e, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ['orders', 'detail', variables.orderId],
      });
      void queryClient.invalidateQueries({ queryKey: ['orders', 'open'] });
      void queryClient.invalidateQueries({ queryKey: ['orders', 'ledger', variables.orderId] });
    },
  });
}

export function useCancelOrder() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, { orderId: string; reason: string }>({
    mutationFn: ({ orderId, reason }) =>
      api.post<void>(`/v1/orders/${orderId}/cancel`, { reason }),
    onSettled: () => invalidateOrders(queryClient),
  });
}

function invalidateOrders(queryClient: ReturnType<typeof useQueryClient>): void {
  void queryClient.invalidateQueries({ queryKey: ['orders'] });
}

const LEDGER_TEXT: Record<string, string> = {
  order_opened: 'Tab opened',
  line_added: 'Added',
  line_qty_changed: 'Quantity changed',
  line_voided: 'Voided',
  course_fired: 'Course fired',
  note_set: 'Note changed',
  guests_changed: 'Guest count changed',
  discount_applied: 'Money off',
  discount_removed: 'Money off undone',
  service_charge_removed: 'Service charge taken off',
  service_charge_restored: 'Service charge put back',
  service_charge_repriced: 'Service charge changed',
  handed_over: 'Handed over',
  table_moved: 'Moved',
  locked_for_payment: 'Bill asked for',
  reopened: 'Reopened',
  order_cancelled: 'Tab cancelled',
  order_closed: 'Tab closed',
  awaiting_collection: 'Paid, waiting to be collected',
  collected: 'Collected',
  guest_attached: 'Guest named on the tab',
  guest_detached: 'Guest taken off the tab',
  takeaway_set: 'Eat in or take away',
};

function text(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null;
}

/**
 * Describe a ledger entry in words a manager would use.
 *
 * `lines` names the dish in entries that only carry its id — a quantity
 * change records which line, not what it was.
 */
export function describeLedgerEntry(entry: LedgerEntry, lines: OrderLine[] = []): string {
  const p = entry.payload;
  const label = LEDGER_TEXT[entry.type] ?? entry.type;
  const name = text(p.name) ?? lines.find((line) => line.id === p.line_id)?.name ?? null;
  const reason = text(p.reason_label) ?? text(p.reason);

  switch (entry.type) {
    case 'line_added':
      return name ? `${label} ${trimQty(text(p.qty) ?? '1')} × ${name}` : label;
    case 'line_voided':
      return `${label} ${name ?? 'a line'}${reason ? ` — ${reason}` : ''}`;
    case 'line_qty_changed':
      return `${name ?? 'A line'}: ${trimQty(String(p.from))} → ${trimQty(String(p.to))}`;
    case 'guests_changed':
      return `${label}: ${String(p.from)} → ${String(p.to)}`;
    case 'course_fired':
      return `Course ${String(p.course_no)} sent to the kitchen`;
    case 'discount_applied':
      return `${label}: ${formatMoney(String(p.amount))}${text(p.line_name) ? ` off ${String(p.line_name)}` : ''}${reason ? ` (${reason})` : ''}`;
    case 'discount_removed':
      return `${label}: ${formatMoney(String(p.amount))} back on`;
    case 'service_charge_removed':
      return `${label}: ${formatMoney(String(p.amount))}${reason ? ` — ${reason}` : ''}`;
    case 'service_charge_restored':
      return `${label}: ${formatMoney(String(p.amount))}`;
    case 'service_charge_repriced':
      return `${label}: ${formatMoney(String(p.from))} → ${formatMoney(String(p.to))}`;
    case 'table_moved':
      return text(p.to) ? `${label} to table ${String(p.to)}` : label;
    case 'order_closed':
      return `${label}, ${formatMoney(String(p.paid_total))} paid`;
    case 'takeaway_set':
      return p.takeaway === true ? 'Changed to take away' : 'Changed to eat in';
    default:
      return reason ? `${label} — ${reason}` : label;
  }
}

/** "2.000" reads as a measurement. Order quantities are counts. */
export function trimQty(qty: string): string {
  const value = Number(qty);
  if (!Number.isFinite(value)) return qty;
  return String(value);
}

/**
 * Hand a tab to somebody else.
 *
 * Only `served_by` moves. Who opened it is what the seller report groups on
 * and has to keep meaning "who started this", or a table passed on at nine
 * o'clock rewrites somebody's figures for the whole evening.
 */
export function useHandOver() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<Order, ApiError, { orderId: string; toUserId: string }>({
    mutationFn: ({ orderId, toUserId }) =>
      api.post<Order>(`/v1/orders/${orderId}/hand-over`, { to_user_id: toUserId }),
    onSuccess: (order) => {
      queryClient.setQueryData(['orders', 'detail', order.id], order);
      void queryClient.invalidateQueries({ queryKey: ['orders'] });
      void queryClient.invalidateQueries({ queryKey: ['floor'] });
    },
  });
}

/**
 * The counter queue: paid for, not yet handed over. Oldest first.
 *
 * Polled harder than the tab list, because this is a list of people standing
 * in front of somebody. Deliberately not asked for a business day — one paid
 * for at ten to midnight is still somebody waiting at five past, and the same
 * default puts an order that was never collected at the top of tomorrow's
 * queue instead of nowhere.
 */
export function useCollectionQueue(locationId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: ['orders', 'collection', locationId ?? null],
    queryFn: ({ signal }) =>
      api.get<Order[]>(`/v1/orders/collection?location_id=${locationId}`, signal),
    enabled: Boolean(locationId),
    staleTime: 3_000,
    refetchInterval: 8_000,
  });
}

/** Hand the bag across. This is where a counter order finishes. */
export function useCollect() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<Order, ApiError, string>({
    mutationFn: (orderId) => api.post<Order>(`/v1/orders/${orderId}/collect`, {}),
    onSuccess: (order) => {
      queryClient.setQueryData(['orders', 'detail', order.id], order);
      invalidateOrders(queryClient);
    },
  });
}
